import React, {FC, ReactElement, useMemo} from 'react';
import {Space, Typography} from "antd";
import {SearchField} from "../../../components/DataTable/SearchField";
import {ExportButton} from "../../../components/DataTable/ExportButton";
import {Filter, QueryFilter} from "../../../components/DataTable/Filters/types";
import {FilterButton} from "../../../components/DataTable/FilterButton";
import {DataTableCard} from "../../../components/DataTable/DataTableCard";
import {ColumnsType} from "antd/lib/table";
import {Student} from "../../../types";
import moment from "moment";
import {useParams} from "react-router-dom";
import {NumericFilter} from "../../../components/DataTable/Filters/NumericFilter";
import {DATE_FORMAT} from "../../../constants";
import {DateFilter} from "../../../components/DataTable/Filters/DateFilter";
import {Translate, useLabels} from "../../../hooks/Labels";
import isNumeric from "antd/lib/_util/isNumeric";
import {useOrganisation} from "../../../layout/OrganisationInformationProvider";
import {PreserveQueryLink as Link} from "../../../components/PreserveQueryLink";

const {Text} = Typography;

const filters = (canViewIndividualData: boolean): Filter<any>[] => [
    ...(canViewIndividualData ? [{
        attribute: 'average_score',
        title: 'Score',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt'],
            unit: '%'
        },
    }] : []) as Filter<any>[],
    {
        attribute: 'course_completion',
        title: 'Completion',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt'],
            unit: '%'
        },
    },
    {
        attribute: 'attempts',
        title: 'Attempts',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt']
        },
    },
    {
        attribute: 'latest_attempt',
        title: 'Last Attempted',
        component: DateFilter,
        config: {
            filters: ['lt', 'gt', 'range']
        },
    },
];

const columns = (t: Translate, canViewIndividualData: boolean, canViewUserPii: boolean): ColumnsType<Student> => [
    ...(canViewUserPii ? [{
        title: 'Name',
        dataIndex: 'full_name',
        key: 'full_name',
        render: (text, record) => <Link to={`/students/${record.id}`}>{text}</Link>
    }] : [{
        title: t('student'),
        dataIndex: 'id',
        key: 'id',
        render: (value, record) => <Link to={`/students/${record.id}`}>{`${t('student')} ${value}`}</Link>
    }]) as ColumnsType<Student>,
    ...(canViewIndividualData ? [{
        title: 'Score',
        dataIndex: 'average_score',
        key: 'average_score',
        render: (value) => isNumeric(value) ? `${value}%` : 'N/A',
        sorter: true
    }] : []) as  ColumnsType<Student>,
    {
        title: 'Completion',
        dataIndex: 'course_completion',
        key: 'course_completion',
        render: (value) => `${value}%`,
        sorter: true
    },
    {
        title: 'Attempts',
        dataIndex: 'attempts',
        key: 'attempts',
        sorter: true
    },

    {
        title: 'Most Recent Activity',
        dataIndex: 'latest_attempt',
        key: 'latest_attempt',
        sorter: true,
        render: (value) => value ? moment(value).format(DATE_FORMAT):
            '-'
    }
]

export const StudentsDataTableCard: FC = (): ReactElement => {
    const {id} = useParams();
    const {t} = useLabels();
    const {
        can_view_individual_data: canViewIndividualData,
        can_view_user_pii: canViewUserPii
    } = useOrganisation();
    const pageFilters = filters(canViewIndividualData);
    const pageColumns = columns(t, canViewIndividualData, canViewUserPii);

    const defaultFilters = useMemo(() => ({
        cohorts: id || '',
    }), [id]);

    return (
        <DataTableCard
            title={<Space size='large'>
                <Text>{t('student_pl')}</Text>
                {canViewUserPii && <SearchField placeholder={`${t('student')} name or email`}/>}
            </Space>}
            extra={<Space>
                <ExportButton
                    url='/reports/students/'
                    filenamePrefix='students'
                    defaultFilters={defaultFilters}
                    searchParamFilters={[QueryFilter.COURSES]}
                />
                <FilterButton filters={pageFilters}/>
            </Space>}
            dataTable={{
                searchParamFilters: [QueryFilter.COURSES],
                defaultFilters,
                columns: pageColumns,
                query: `/reports/students/`
            }}/>
    );
};
